// Merchant Sales Report export (MM-3168): the Export button and its CSV / EXCEL menu, styled like GridTable's.
// The backend builds the file for the selected merchant's whole hierarchy. Up to 3000 merchants it returns a URL
// that we open straight away; above that it emails a download link valid for 6 hours, and we say so.
import { useEffect, useRef, useState } from "react";
import ButtonWithImg from "../../../components/buttons/ButtonWithImg";

export type ExportFormat = "csv" | "xlsx";

const EXPORT_OPTIONS: { label: string; value: ExportFormat; textColorClass: string }[] = [
  { label: "CSV", value: "csv", textColorClass: "text-positive" },
  { label: "EXCEL", value: "xlsx", textColorClass: "text-positive" },
];

interface ExportMenuProps {
  /** Called before the menu opens; returns an error message when export isn't possible yet. */
  validate: () => string | null;
  onInvalid: (message: string) => void;
  onExport: (format: ExportFormat) => Promise<void>;
  disabled?: boolean;
}

const ExportMenu = ({ validate, onInvalid, onExport, disabled }: ExportMenuProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    // Capture phase so scrolls inside nested scroll containers (the table) also close it, as in GridTable.
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const onButtonClick = () => {
    const error = validate();
    if (error) {
      setOpen(false);
      onInvalid(error);
      return;
    }
    setOpen((value) => !value);
  };

  const onOptionClick = async (format: ExportFormat) => {
    setOpen(false);
    setLoading(true);
    try {
      await onExport(format);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <ButtonWithImg
        buttonName="Export"
        disabledButton={disabled}
        ImgSrc={disabled ? "loading-export" : "export"}
        testId="merchant-sales-export"
        className="w-[110px] bg-p_primary px-4 text-white"
        buttonOnclick={onButtonClick}
        loading={loading}
      />
      {open && (
        <div className="absolute right-0 z-70 mt-1 w-[110px] rounded-lg border border-s_dark_hover bg-white">
          <ul className="flex flex-col">
            {EXPORT_OPTIONS.map((option) => (
              <li
                key={option.value}
                data-testid={option.value}
                className={`cursor-pointer rounded-lg p-2 text-body2 ${option.textColorClass} hover:bg-p_light`}
                onClick={() => onOptionClick(option.value)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
